import { BaseSensorWebController } from '../../controller/BaseSensorWebController';
import { BaseSensorWebComponent } from '../../BaseSensorWebComponent';
import { O365SensorView } from '../view/O365SensorView';
import {
  WEB_COMPONENT_GZ_DIALOG,
  WEB_COMPONENT_INTEGRATION_CREATE_PANEL,
  WEB_COMPONENT_INTEGRATION_EDIT_INTEGRATION,
  WEB_COMPONENT_SENSOR_O365_AUDIT,
  WEB_COMPONENT_SENSOR_O365_MAIL,
} from '../../../../../webcomponents.constants';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { O365AuditFields, O365MailFields } from '../../../constants';

export class O365SensorController extends BaseSensorWebController {
  public component: BaseSensorWebComponent;
  public view: O365SensorView;
  public viewConstructor: typeof O365SensorView;

  static getIntegrationPanelComponent() {
    const dialogComponent = document.querySelector(WEB_COMPONENT_GZ_DIALOG);
    const integrationCreatePanelComponent = dialogComponent?.shadowRoot?.querySelector(
      WEB_COMPONENT_INTEGRATION_CREATE_PANEL
    );
    return integrationCreatePanelComponent?.shadowRoot?.querySelector(WEB_COMPONENT_INTEGRATION_EDIT_INTEGRATION);
  }

  static reuseO365Field(sourceSelector: string, destinationSelector: string): void {
    if (sourceSelector && destinationSelector) {
      const sourceField = O365SensorController.getSourceField(sourceSelector);
      const destinationField = O365SensorController.getDestinationField(destinationSelector);

      if (sourceField && destinationField && !sourceField.disabled) {
        destinationField.value = sourceField.value;
      }
    }
  }

  private static getSourceField(sourceSelector: string): GzFormFieldWebComponent {
    const integrationEditPanelComponent = O365SensorController.getIntegrationPanelComponent();
    const o365MailComponent = integrationEditPanelComponent?.shadowRoot?.querySelector(WEB_COMPONENT_SENSOR_O365_MAIL);
    const o365AuditComponent = integrationEditPanelComponent?.shadowRoot?.querySelector(
      WEB_COMPONENT_SENSOR_O365_AUDIT
    );

    let sourceField = o365MailComponent?.shadowRoot?.querySelector('#' + sourceSelector) as GzFormFieldWebComponent;
    if (Object.values(O365AuditFields).includes(sourceSelector as O365AuditFields)) {
      sourceField = o365AuditComponent?.shadowRoot?.querySelector('#' + sourceSelector) as GzFormFieldWebComponent;
    }

    return sourceField;
  }

  private static getDestinationField(destinationSelector: string): GzFormFieldWebComponent {
    const integrationEditPanelComponent = O365SensorController.getIntegrationPanelComponent();
    const o365MailComponent = integrationEditPanelComponent?.shadowRoot?.querySelector(WEB_COMPONENT_SENSOR_O365_MAIL);
    const o365AuditComponent = integrationEditPanelComponent?.shadowRoot?.querySelector(
      WEB_COMPONENT_SENSOR_O365_AUDIT
    );

    let destinationField = o365MailComponent?.shadowRoot?.querySelector(
      '#' + destinationSelector
    ) as GzFormFieldWebComponent;
    if (Object.values(O365AuditFields).includes(destinationSelector as O365AuditFields)) {
      destinationField = o365AuditComponent?.shadowRoot?.querySelector(
        '#' + destinationSelector
      ) as GzFormFieldWebComponent;
    }

    return destinationField;
  }

  /**
   * Check if we have the same value into sourceField and destinationField (e.g: audit field and mail field)
   * should be used to compare
   *    - tenantId from mail with tenantId from audit
   *    - applicationId from mail with applicationId from audit
   *    - clientSecret from mail with clientSecret from audit
   * @param {string} sourceSelector
   * @param {string} destinationSelector
   * @returns {boolean}
   */
  static isSameValueIntoFields(sourceSelector: string, destinationSelector: string): boolean {
    if (sourceSelector && destinationSelector) {
      const sourceField = O365SensorController.getSourceField(sourceSelector);
      const destinationField = O365SensorController.getDestinationField(destinationSelector);

      return sourceField.value === destinationField.value;
    }

    return false;
  }

  /**
   * Check if sensor is enabled based on checkbox value
   * @param {string} component
   * @returns {boolean}
   */
  static isSensorEnabled(component: string): boolean {
    let o365Component;
    const integrationEditPanelComponent = O365SensorController.getIntegrationPanelComponent();

    switch (component) {
      case WEB_COMPONENT_SENSOR_O365_MAIL:
        o365Component = integrationEditPanelComponent?.shadowRoot?.querySelector(WEB_COMPONENT_SENSOR_O365_MAIL);
        break;

      case WEB_COMPONENT_SENSOR_O365_AUDIT:
        o365Component = integrationEditPanelComponent?.shadowRoot?.querySelector(WEB_COMPONENT_SENSOR_O365_AUDIT);
        break;

      default:
        return false;
    }

    const sensorSelectWrapper = o365Component?.shadowRoot?.querySelector(
      '.sensor-select-wrapper'
    ) as GzFormFieldWebComponent;
    if (!sensorSelectWrapper) {
      return false;
    }

    const enabledCheckbox = sensorSelectWrapper.Input as GzCheckboxWebComponent;
    if (!enabledCheckbox) {
      return false;
    }

    return enabledCheckbox.checked;
  }

  /**
   * Set readOnly for tenantId
   * @param fieldSelector
   * @param {boolean} readOnlyValue
   */
  static setReadOnlyForO365AuditField(fieldSelector: string, readOnlyValue: boolean): void {
    if (fieldSelector) {
      const integrationEditPanelComponent = O365SensorController.getIntegrationPanelComponent();
      const o365AuditComponent = integrationEditPanelComponent?.shadowRoot?.querySelector(
        WEB_COMPONENT_SENSOR_O365_AUDIT
      );
      const fieldComponent = o365AuditComponent?.shadowRoot?.querySelector(
        '#' + fieldSelector
      ) as GzFormFieldWebComponent;

      if (!fieldComponent) {
        return;
      }

      fieldComponent.readonly = readOnlyValue;
    }
  }

  /**
   * Disabled O365 reuse data option
   * @param {boolean} disableValue
   */
  static disableO365AuditReuseData(disableValue: boolean): void {
    const reuseCheckbox = O365SensorController.getReuseCheckboxComponent();

    // uncheck and disable the reuse option
    const reuseInput = reuseCheckbox.Input as GzCheckboxWebComponent;
    reuseInput.checked = false;

    reuseCheckbox.disabled = disableValue;
  }

  /**
   * Get the checkbox value of "Use same details for Audit"
   * @returns {boolean}
   */
  static isReuseDataChecked(): boolean {
    const reuseCheckboxComponent = O365SensorController.getReuseCheckboxComponent();
    const reuseInput = reuseCheckboxComponent.Input as GzCheckboxWebComponent;
    return reuseInput ? reuseInput.checked : false;
  }

  static getReuseCheckboxComponent(): GzFormFieldWebComponent {
    const integrationEditPanelComponent = O365SensorController.getIntegrationPanelComponent();
    const o365AuditComponent = integrationEditPanelComponent?.shadowRoot?.querySelector(
      WEB_COMPONENT_SENSOR_O365_AUDIT
    );
    return o365AuditComponent?.shadowRoot?.querySelector('.sensor-reuse-wrapper') as GzFormFieldWebComponent;
  }

  /**
   * Reuse O365 mail fields
   * @param {string} onlyForField
   */
  static reuseO365MailDataIntoAuditFields(onlyForField?: string): void {
    if (
      !this.isSensorEnabled(WEB_COMPONENT_SENSOR_O365_MAIL) ||
      !this.isSensorEnabled(WEB_COMPONENT_SENSOR_O365_AUDIT)
    ) {
      return;
    }

    if (typeof onlyForField !== 'string' || !onlyForField) {
      // copy applicationId, tenant id and clientSecret
      this.reuseO365FieldForApplicationId();
      this.reuseO365FieldForTenantId(O365MailFields.TENANT);
      this.reuseO365FieldForClientSecret();
      return;
    }

    // copy only applicationId
    if (onlyForField === O365MailFields.APPLICATION) {
      this.reuseO365FieldForApplicationId();
      return;
    }

    // copy only clientSecret
    if (onlyForField === O365MailFields.CLIENT_SECRET) {
      this.reuseO365FieldForClientSecret();
      return;
    }

    // copy only tenantId
    if (onlyForField === O365MailFields.TENANT) {
      this.reuseO365FieldForTenantId(onlyForField);
      return;
    }
  }

  /**
   * If reuse data is checked then copy values of applicationId from mail to audit
   */
  static reuseO365FieldForApplicationId(): void {
    if (!this.isReuseDataChecked()) {
      this.setReadOnlyForO365AuditField(O365AuditFields.APPLICATION, false);
      return;
    }

    this.reuseO365Field(O365MailFields.APPLICATION, O365AuditFields.APPLICATION);
    this.setReadOnlyForO365AuditField(O365AuditFields.APPLICATION, true);
  }

  /**
   * Copy tenantId
   */
  static reuseO365FieldForTenantId(sourceField?: string): void {
    if (sourceField === O365MailFields.TENANT) {
      this.reuseO365Field(O365MailFields.TENANT, O365AuditFields.TENANT);
    }

    if (sourceField === O365AuditFields.TENANT) {
      this.reuseO365Field(O365AuditFields.TENANT, O365MailFields.TENANT);
    }

    if (!this.isReuseDataChecked()) {
      this.setReadOnlyForO365AuditField(O365AuditFields.TENANT, false);
      return;
    }

    this.setReadOnlyForO365AuditField(O365AuditFields.TENANT, true);
  }

  /**
   * If reuse data is checked then copy values of client secret from mail to audit
   */
  static reuseO365FieldForClientSecret(): void {
    if (!O365SensorController.isReuseDataChecked()) {
      this.setReadOnlyForO365AuditField(O365AuditFields.CLIENT_SECRET, false);
      return;
    }

    this.reuseO365Field(O365MailFields.CLIENT_SECRET, O365AuditFields.CLIENT_SECRET);
    this.setReadOnlyForO365AuditField(O365AuditFields.CLIENT_SECRET, true);
  }

  public resetData(): void {
    super.resetData();
    this.view.dom.$applicationId.resetField();
    this.view.dom.$tenantId.resetField();
    this.view.dom.$clientSecret.resetField();
  }
}
